// Audio utility functions for generating copyright-free sounds
class AudioGenerator {
    constructor() {
        this.audioContext = null;
        this.initAudioContext();
    }

    initAudioContext() {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (error) {
            console.warn('Web Audio API not supported:', error);
        }
    }

    // Resume audio context if suspended (required for user interaction)
    async resumeAudioContext() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
    }

    // Generate a "ting" sound for scoring
    generateTingSound() {
        if (!this.audioContext) return null;

        const buffer = this.audioContext.createBuffer(1, this.audioContext.sampleRate * 0.3, this.audioContext.sampleRate);
        const data = buffer.getChannelData(0);

        // Generate a bell-like sound with harmonics
        for (let i = 0; i < data.length; i++) {
            const t = i / this.audioContext.sampleRate;
            const envelope = Math.exp(-t * 8); // Decay envelope
            
            // Multiple harmonics for a bell-like sound
            const fundamental = Math.sin(2 * Math.PI * 800 * t);
            const harmonic2 = Math.sin(2 * Math.PI * 1200 * t) * 0.5;
            const harmonic3 = Math.sin(2 * Math.PI * 1600 * t) * 0.25;
            
            data[i] = (fundamental + harmonic2 + harmonic3) * envelope * 0.3;
        }

        return buffer;
    }

    // Generate "tana ten tan tan" sound for game over - fast Mario-style
    generateGameOverSound() {
        if (!this.audioContext) return null;

        const buffer = this.audioContext.createBuffer(1, this.audioContext.sampleRate * 1.2, this.audioContext.sampleRate);
        const data = buffer.getChannelData(0);

        // Fast descending notes like Mario game over: quick "ta-na-ten-tan"
        const notes = [
            { freq: 523, start: 0, duration: 0.15 },     // C5 - "ta" (quick)
            { freq: 440, start: 0.15, duration: 0.15 },  // A4 - "na" (quick)
            { freq: 349, start: 0.3, duration: 0.2 },    // F4 - "ten" (slightly longer)
            { freq: 294, start: 0.5, duration: 0.7 }     // D4 - "tan" (final dramatic note)
        ];

        for (let i = 0; i < data.length; i++) {
            const t = i / this.audioContext.sampleRate;
            let sample = 0;

            notes.forEach(note => {
                if (t >= note.start && t < note.start + note.duration) {
                    const noteTime = t - note.start;
                    
                    // Mario-style envelope - quick attack, fast decay for dramatic effect
                    let envelope;
                    if (note.freq === 294) { // Final note gets special treatment
                        envelope = Math.exp(-noteTime * 2); // Slower decay for dramatic ending
                    } else {
                        envelope = Math.exp(-noteTime * 6); // Fast decay for quick notes
                    }
                    
                    // Add square wave characteristics for classic game sound
                    const fundamental = Math.sin(2 * Math.PI * note.freq * noteTime);
                    const squareWave = Math.sign(Math.sin(2 * Math.PI * note.freq * noteTime));
                    
                    // Mix for classic video game sound
                    const mixedWave = (fundamental * 0.7) + (squareWave * 0.3);
                    
                    sample += mixedWave * envelope * 0.25;
                }
            });

            data[i] = Math.max(-1, Math.min(1, sample)); // Clamp to prevent distortion
        }

        return buffer;
    }

    // Play a buffer
    playBuffer(buffer, loop = false, volume = 1) {
        if (!this.audioContext || !buffer) return null;

        const source = this.audioContext.createBufferSource();
        const gainNode = this.audioContext.createGain();
        
        source.buffer = buffer;
        source.loop = loop;
        gainNode.gain.value = volume;
        
        source.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        source.start();
        return source;
    }
}

// Create singleton instance
const audioGenerator = new AudioGenerator();

// Pre-generate sound buffers
let tingBuffer = null;
let gameOverBuffer = null;

// Initialize buffers
const initializeAudioBuffers = () => {
    tingBuffer = audioGenerator.generateTingSound();
    gameOverBuffer = audioGenerator.generateGameOverSound();
};

// Initialize on first user interaction
let audioInitialized = false;
const initializeAudio = async () => {
    if (!audioInitialized) {
        await audioGenerator.resumeAudioContext();
        initializeAudioBuffers();
        audioInitialized = true;
    }
};

// Export functions to play sounds
export const playTingSound = async () => {
    await initializeAudio();
    if (tingBuffer) {
        audioGenerator.playBuffer(tingBuffer, false, 0.7);
    }
};

export const playGameOverSound = async () => {
    await initializeAudio();
    if (gameOverBuffer) {
        audioGenerator.playBuffer(gameOverBuffer, false, 0.8);
    }
};

export { audioGenerator }; 